import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { Avatar, Typography, Box } from '@mui/material'
import { useDonorContext } from '@/context'
import { forcyteService, sessionStorageService } from '@/services'
import { ROUTES, STORAGE_KEYS } from '@/constants'
import { ExpandMoreIcon, AccountIcon } from '@/icons'
import { CommonButton, CommonMenu } from '@/components/common'
import { useLogout } from '@/hooks'

export const UserMenu = ({ id = 'donor-user-menu', donor = {}, showMenu }) => {
  const navigate = useNavigate()
  const { logout } = useLogout()
  const { setDonor } = useDonorContext()

  const [anchorEl, setAnchorEl] = useState(null)
  const isOpen = Boolean(anchorEl)

  const fullName = useMemo(() => {
    if (!donor?.id) return 'Guest'

    const name = []

    if (donor?.first_name) name.push(donor.first_name)
    if (donor?.last_name) name.push(donor.last_name)
    return name.join(' ')
  }, [donor])

  const initials = useMemo(() => {
    if (!donor?.id || fullName === 'Guest') return 'G'

    const parts = fullName.trim().split(' ')
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase()
    }
    return (
      parts[0].charAt(0).toUpperCase() +
      parts[parts.length - 1].charAt(0).toUpperCase()
    )
  }, [donor, fullName])

  const avatarUrl = donor?.avatar_url

  const menuItems = useMemo(
    () =>
      [
        {
          label: 'Reset Password',
          onClick: () => navigate(ROUTES.RESET_PASSWORD),
        },
        {
          label: 'Log Out',
          onClick: () => handleLogout(),
        },
      ].map((item, index) => ({ ...item, key: `${id}-menu-${index}` })),
    [id]
  )

  const handleLogout = async () => {
    await logout()
    sessionStorageService.removeItem(STORAGE_KEYS.RESCHEDULE_APPOINTMENT_ID)
    forcyteService.clearToken()
    forcyteService.refresh()
    setDonor({})
  }

  const handleOpen = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleToggle = event => {
    if (!showMenu) return

    if (isOpen) handleClose()
    else handleOpen(event)
  }

  const handleKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleToggle(event)
    }
  }

  return (
    <>
      {donor && donor.id && (
        <CommonButton
          id={id}
          variant='text'
          color='primary'
          startIcon={
            <>
              {avatarUrl ? (
                <Avatar
                  src={avatarUrl || undefined}
                  sx={{ width: 30, height: 30 }}
                ></Avatar>
              ) : (
                <AccountIcon />
              )}
            </>
          }
          endIcon={
            showMenu && (
              <ExpandMoreIcon
                fontSize='small'
                sx={{
                  transform: isOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 120ms',
                }}
              />
            )
          }
          aria-haspopup='menu'
          aria-expanded={isOpen ? 'true' : 'false'}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          label={
            <Typography
              variant='body1'
              color='primary.main'
              sx={{ fontWeight: 600, userSelect: 'none' }}
            >
              <Box
                component='span'
                sx={{
                  display: { xs: 'block', md: 'none' },
                }}
              >
                {initials}
              </Box>
              <Box
                component='span'
                sx={{
                  display: { xs: 'none', md: 'block' },
                }}
              >
                {fullName}
              </Box>
            </Typography>
          }
        />
      )}
      <CommonMenu
        id={`${id}-menu`}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        items={menuItems}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      />
    </>
  )
}

UserMenu.propTypes = {
  id: PropTypes.string,
  donor: PropTypes.object,
  showMenu: PropTypes.bool,
}
